import { inject, injectable } from 'inversify'
import Debug from 'debug';
import _ from 'lodash'
import { DualCommandQueue }from '@/app/shared/dual-command-queue'
import { CancelToken, delay } from '@/app/shared/utils';
import { cloneRemovingVueObserver, checkVueObserver } from '@/app/shared/utils/vue-observer-utils';
import { TrackingService2, TrackingService2Symbol, createCommand, createQuery } from '@/app/tracking/api';
import { QueryInteractionResultsTrackingQuery, QueryInteractionStatesTrackingQuery,
         UpdateInteractionStateTrackingCommand } from '../tracking/api';
import { InteractionStateStoreModule, InteractionStoreModuleSymbol } from '../store'
import { UpdateInteractionResultTrackingCommand } from '../tracking/api/update-interaction-result-tracking-command';

export const InteractionStateServiceSymbol = Symbol.for('InteractionService')

const debug = Debug('interaction-state-service')

@injectable()
export class InteractionStateService {

  private queue: DualCommandQueue;

  constructor(
    @inject(InteractionStoreModuleSymbol)
    private interactionStore: InteractionStateStoreModule,
    @inject(TrackingService2Symbol)
    private trackingService: TrackingService2) {

    this.queue = new DualCommandQueue('interaction-service')
  }

  public start() {
    debug('starting InteractionStateService');

    this.queue.start();
    this.queue.enqueueRemote('query-results-tracking', (cancel) => this.queryInteractionResultsFromTracking(cancel));
    this.queue.enqueueRemote('query-states-tracking', (cancel) => this.queryInteractionStatesFromTracking(cancel));
  }

  public async stop() {
    debug('stopping InteractionStateService');

    await this.queue.stop()
  }

  updateInteractionState(payload: { id: string, type: string, state: unknown},
                         options: { saveInTracking: boolean }) {
    debug('queue update-state', payload)

    checkVueObserver(payload)
    
    this.queue.enqueueLocal('update-state', async () => {
      debug('udpate-state', payload)

      // should clone, because by adding to the store it gets being tracked
      const intoStore = {
        id: payload.id,
        type: payload.type,
        state: cloneRemovingVueObserver(payload.state)
      }
      this.interactionStore.updateState(intoStore)

      if (options.saveInTracking) {
        debug('updating interaction state in tracking', payload.id, payload.state);
        const trackingCommand = createCommand<UpdateInteractionStateTrackingCommand>('update-interaction-state', {
          id: payload.id,
          type: payload.type,
          state: payload.state
        })
        this.trackingService.execute(trackingCommand)
      }
    })
  }

  updateInteractionResult(payload: { id: string, type: string,
                                     progress: number, status: 'incomplete' | 'completed'},
                          options: { saveInTracking: boolean }) {
    debug('queue update-result', payload)

    payload = checkVueObserver(payload)
    
    this.queue.enqueueLocal('update-result', async () => {

      debug('udpate-result', payload)

      // should clone, because by adding to the store it gets being tracked
      const intoStore = {
        id: payload.id,
        progress: payload.progress,
        status: payload.status
      }
      this.interactionStore.updateResult(intoStore)

      if (options.saveInTracking) {
        debug('updating interaction result in tracking', payload.id, payload.progress, payload.status);
        const trackingCommand = createCommand<UpdateInteractionResultTrackingCommand>(
          'update-interaction-result', {
            id: payload.id,
            type: payload.type,
            progress: payload.progress,
            status: payload.status
          })
        this.trackingService.execute(trackingCommand)
      }
    })
  }

  private async queryInteractionStatesFromTracking(cancel: CancelToken) {
    debug(`querying interaction states from tracking`);

    this.interactionStore.startStatesRemoteQuerying()

    //await delay(10000)
    
    const query = createQuery<QueryInteractionStatesTrackingQuery>('query-interaction-states', {})
    const result = await this.trackingService.query(query, cancel)

    const cloned = _.cloneDeep(result.interactionStates)
    debug(`interaction states tracking query finished: %j`, cloned);
    this.interactionStore.finishStatesRemoteQuerying(cloned)
  }

  private async queryInteractionResultsFromTracking(cancel: CancelToken) {
    debug(`querying interaction results from tracking`);

    this.interactionStore.startResultsRemoteQuerying()

    const query = createQuery<QueryInteractionResultsTrackingQuery>('query-interaction-results', {})
    const result = await this.trackingService.query(query, cancel)

    const cloned = _.cloneDeep(result.interactionResults)
    debug(`interaction results tracking query finished: %j`, cloned);
    this.interactionStore.finishResultsRemoteQuerying(cloned)
  }
}
